import { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  createRule,
  editRule,
  updateRuleStatus,
} from "~/services/businessRule.services";
import Select from "react-select";
import { SubmitButton as Button } from "~/components/form/submit-button";
import ConfirmActionModal from "./ConfirmActionModal.tsx";

const ArrayField = ({ field, control, renderField, fieldName }: any) => {
  const {
    fields: arrayItems,
    append,
    remove,
  } = useFieldArray({
    control,
    name: fieldName,
  });

  return (
    <div key={fieldName} className="mb-4">
      <h2 className="text-sm font-semibold text-gray-800 mb-1">
        {field.label}
      </h2>
      {arrayItems.map((item, idx) => {
        const isEven = idx % 2 === 0;
        const bgClass = isEven ? "bg-gray-100" : "bg-white";

        return (
          <div
            key={item.id}
            className={`grid grid-cols-[200px_1fr_120px_40px_40px] items-start gap-2 px-2 py-2 pt-4 ${bgClass}`}
          >
            {field.fields.map((subField: any) => (
              <div key={subField.id}>
                {renderField(subField, `${fieldName}[${idx}]`)}
              </div>
            ))}
            {field.id === "businessrulecondition" && (
              <h2 className="text-sm font-semibold text-gray-800 pt-[12px]">
                AND
              </h2>
            )}
            {!idx && (
              <button
                type="button"
                onClick={() => append({})}
                className={`flex justify-center items-center mt-1 ${
                  arrayItems.length >= 4
                    ? "bg-gray-50"
                    : "bg-blue-50 hover:bg-blue-100"
                } text-blue-600 rounded p-1 text-xl`}
                title="Add"
                disabled={arrayItems.length >= 4}
              >
                +
              </button>
            )}
            {!!idx && (
              <button
                type="button"
                onClick={() => remove(idx)}
                className="flex justify-center items-center bg-red-50 text-red-600 rounded p-1  mt-1 hover:bg-red-100 text-xl"
                title="Remove"
              >
                −
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DynamicForm = ({
  closeModal,
  schema,
  initialData,
  fieldToOperatorMap,
  type,
  ruleId,
}: any) => {
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: initialData || {},
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"terminate" | "delete" | null>(
    null
  );

  const openModal = (type: "terminate" | "delete") => {
    setModalType(type);
    setModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setModalOpen(false);
    setModalType(null);
  };

  const handleConfirm = async (comment?: string) => {
    if (modalType === "terminate") {
      await handleStatusUpdate("Terminated", comment);
    } else if (modalType === "delete") {
      await handleStatusUpdate("Deleted");
    }
    closeModal();
  };

  const onSubmit = async (data: any) => {
    if (type === "edit") {
      const { status } = await editRule({ dbdata: data }, ruleId);
      if (status) {
        closeModal();
      } 
    } else {
      const { status } = await createRule({ dbdata: data });
      if (status) {
        closeModal();
      } 
    }
  };
  const handleStatusUpdate = async (status: string, comment?: string) => {
    await updateRuleStatus(status, ruleId, comment);
    closeModal();
  };

  useEffect(() => {
    reset(initialData || {});
  }, [initialData, reset]);

  const renderField = (field: any, parentName = "") => {
    const fieldName = parentName ? `${parentName}.${field.id}` : field.id;
    switch (field.type) {
      case "text":
      case "number": {
        let fieldRegex, selectedFieldName;
        let rules = {
          required: `${field.label} is required`,
        };
        if (field.id === "fieldvalue") {
          selectedFieldName = watch(`${parentName}.fieldname`);
          const regexString = fieldToOperatorMap?.[selectedFieldName]?.regex;
          const errorMessage =
            fieldToOperatorMap?.[selectedFieldName]?.errorMessage;
          if (regexString) {
            try {
              fieldRegex = new RegExp(regexString);
            } catch (err) {
              console.warn("Invalid regex pattern:", regexString);
            }
          }
          rules = {
            ...rules,
            ...{
              pattern: {
                value: fieldRegex,
                message: errorMessage,
              },
            },
          };
        }
        return (
          <div key={fieldName} className="mb-2">
            <Controller
              name={fieldName}
              control={control}
              rules={rules}
              render={({ field: controllerField, fieldState }) => (
                <div>
                  <input
                    min={field.min}
                    max={field.max}
                    type={field.type}
                    {...controllerField}
                    onChange={(e) => {
                      const value =
                        field.type === "number"
                          ? Number(e.target.value)
                          : e.target.value;
                      controllerField.onChange(value);
                    }}
                    placeholder={field.label}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {fieldState?.error?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        );
      }
      case "select": {
        let options = field.options || {};
        if (
          field.id === "fieldoperator" &&
          parentName.includes("businessrulecondition")
        ) {
          const selectedFieldName = watch(`${parentName}.fieldname`);
          const validOps =
            fieldToOperatorMap?.[selectedFieldName]?.operator || "";
          options = Object.fromEntries(
            Object.entries(field.options).filter(([op]) =>
              validOps.includes(op)
            )
          );
        }

        const selectOptions = Object.entries(
          options as Record<string, string>
        ).map(([value, label]) => ({
          value,
          label,
        }));
        
        return (
          <div key={fieldName} className="mb-2">
            <Controller
              name={fieldName}
              control={control}
              rules={{ required: `${field.label} is required` }}
              render={({ field: controllerField, fieldState }) => {
                if (selectOptions.length && controllerField.value && !selectOptions.find(
                  (opt) => opt.value === controllerField.value
                )) {
                  controllerField.onChange(null)
                }
                return (
                <div>
                  <Select
                    {...controllerField}
                    options={selectOptions}
                    placeholder={field.label}
                    onChange={(selectedOption) => {
                      controllerField.onChange(selectedOption?.value);
                    }}
                    value={
                      selectOptions.find(
                        (opt) => opt.value === controllerField.value
                      ) || null
                    }
                    classNamePrefix="react-select"
                    menuPlacement="auto"
                    menuPosition="absolute"
                    menuShouldScrollIntoView={false}
                  />
                  {fieldState?.error?.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}}
            />
          </div>
        );
      }

      case "array":
        return (
          <ArrayField
            key={fieldName}
            field={field}
            control={control}
            renderField={renderField}
            fieldName={fieldName}
          />
        );

      case "object":
        return (
          <div key={fieldName}>
            <h2 className="text-lg font-semibold text-green-600 mb-2">
              {field.label}
            </h2>
            <div className="space-y-2">
              {field.fields.map((subField: any) =>
                renderField(subField, fieldName)
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderForm = (schema: any) =>
    schema.fields.map((field: any) => renderField(field));

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-2xl mx-auto p-6 bg-white shadow-md rounded-lg space-y-4"
    >
      {renderForm(schema)}

      <div className="flex justify-end space-x-2">
        <Button
          type="submit"
          variant={"primary"}
          size={"small"}
          className="flex-1 text-sm rounded me-2 py-2"
          disabled={
            type === "edit" &&
            !["Draft", "Tested"].includes(initialData?.businessrule?.status)
          }
        >
          Save
        </Button>
        {type === "edit" && (
          <>
            <Button
              type="button"
              onClick={() => handleStatusUpdate("Tested")}
              variant={"secondary"}
              size={"small"}
              className="flex-1 text-sm rounded me-2 py-2"
              disabled={initialData?.businessrule?.status !== "Draft"}
            >
              Tested
            </Button>

            <Button
              type="button"
              onClick={() => handleStatusUpdate("Finalised")}
              variant={"secondary"}
              size={"small"}
              className="flex-1 text-sm rounded me-2 py-2"
              disabled={initialData?.businessrule?.status !== "Tested"}
            >
              Finalise
            </Button>

            <Button
              type="button"
              onClick={() => {
                if (initialData?.businessrule?.status === "Finalised") {
                  openModal("terminate");
                } else {
                  openModal("delete");
                }
              }}
              variant={"secondary"}
              size={"small"}
              className="flex-1 text-sm rounded me-2 py-2"
              disabled={["Deleted", "Terminated"].includes(initialData?.businessrule?.status)}
            >
              {initialData?.businessrule?.status === "Finalised"
                ? "Terminate"
                : "Delete"}
            </Button>
          </>
        )}
      </div>
      <ConfirmActionModal
        isOpen={modalOpen}
        actionType={modalType}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirm}
      />
    </form>
  );
};

export default DynamicForm;
